import { useState } from 'react';
import { useToast } from '@chakra-ui/react';
import axios from 'axios';

// Update the TranscriptionResult interface to match the one in speechApi.ts
export interface TranscriptionResult {
  text: string;
  confidence?: number;
  s3_url?: string;
  presigned_url?: string;
  s3_key?: string;
  conversation_id?: string;
  created_new_conversation?: boolean;
  message_id?: number;
  error?: string;
}

const useSpeechApi = () => {
  const [isTranscribing, setIsTranscribing] = useState(false);
  const toast = useToast();

  const transcribeAudio = async (
    audioBlob: Blob,
    conversationId?: string,
    userId?: string
  ): Promise<TranscriptionResult> => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      if (conversationId) {
        formData.append('conversation_id', conversationId);
      }
      
      if (userId) {
        formData.append('user_id', userId);
      }

      const response = await axios.post('/api/transcribe', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setIsTranscribing(false);
      return response.data;
    } catch (error) {
      setIsTranscribing(false);
      console.error('Error transcribing audio:', error);
      
      // Show an error toast
      toast({
        title: 'Transcription Error',
        description: error instanceof Error ? error.message : 'Failed to transcribe audio',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      
      return {
        text: '',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };
  
  /**
   * Refreshes a pre-signed URL for an S3 object
   * @param s3Key The S3 key (path in bucket) to generate a pre-signed URL for
   * @param expirationSeconds How long the URL should be valid for (in seconds)
   * @returns Promise with the new pre-signed URL or error
   */
  const refreshPresignedUrl = async (
    s3Key: string,
    expirationSeconds: number = 3600
  ): Promise<{ presigned_url: string } | { error: string }> => {
    try {
      const formData = new FormData();
      formData.append('s3_key', s3Key);
      formData.append('expiration_seconds', String(expirationSeconds));
      
      const response = await axios.post('/api/generate-presigned-url', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success && response.data.presigned_url) {
        return {
          presigned_url: response.data.presigned_url
        };
      } else {
        throw new Error(response.data.error || 'Failed to generate pre-signed URL');
      }
    } catch (error) {
      console.error('Error refreshing pre-signed URL:', error);
      
      // Show an error toast
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to refresh audio URL',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      
      return {
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  return {
    transcribeAudio,
    refreshPresignedUrl,
    isTranscribing,
  };
};

export default useSpeechApi; 